import axios from 'axios';
import {
  BridgeTokenKind,
  deleteAnonymousBridgeTokens,
  isValidToken,
  readBridgeAccessToken,
  readBridgeRefreshToken,
  storeBridgeAccessToken,
  storeBridgeRefreshToken,
} from './bridgeTokenCookies';

/** Sign-out runs this through `resetBridgeTokenProvisioning`, under the commit lock. */
export { deleteUserBridgeTokens } from './bridgeTokenCookies';

export type BridgeTokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: string;
  scope?: string;
};

export type BridgeTokenRequest = {
  /** USG origin without `/api`, e.g. `https://staging.services.unity.com`. */
  usgBaseUrl: string;
  /** Unity ID JWT (`unity_token`) from the session; omit to mint a guest token. */
  unityToken?: string;
};

export type ExchangeBridgeTokenRequest = BridgeTokenRequest & {
  cartId: string;
};

/** Replaces the axios rejection, which carries the bearer headers in its config. */
export class BridgeTokenRequestError extends Error {
  constructor(readonly status?: number) {
    super(`Bridge token: request failed${status ? ` (HTTP ${status})` : ''}.`);
    this.name = 'BridgeTokenRequestError';
  }
}

const COMMERCE_PLATFORM_PATH = '/api/commerce/platform';
const FORM_URLENCODED = 'application/x-www-form-urlencoded';
/**
 * Every provision and every interceptor renewal runs through one queue, so a
 * request left hanging by a blackholed proxy would stall all of them until the
 * OS gave up. Failing lets the queue drain and the error path run.
 */
const TOKEN_REQUEST_TIMEOUT_MS = 15_000;

const parseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

const isSecureOrigin = (url: URL) =>
  url.protocol === 'https:' ||
  (url.protocol === 'http:' &&
    (url.hostname === 'localhost' || url.hostname === '127.0.0.1'));

const isBareOrigin = (url: URL) =>
  url.pathname.replace(/\/+$/, '') === '' &&
  !url.search &&
  !url.hash &&
  !url.username &&
  !url.password;

/**
 * Anything but a bare absolute https origin builds a request URL the caller did
 * not mean: a relative value hits the marketplace's own origin, a
 * protocol-relative one hits whatever host follows the slashes, and the
 * server-side `USG_API` — the nearest value to hand — already ends in `/api`.
 */
export const resolveUsgBaseUrl = (usgBaseUrl: string): string => {
  const parsed = parseUrl(usgBaseUrl?.trim() ?? '');
  if (!parsed || !isSecureOrigin(parsed) || !isBareOrigin(parsed)) {
    throw new Error(
      'Bridge token: usgBaseUrl must be a bare absolute https origin with no path, query or credentials, e.g. https://staging.services.unity.com.'
    );
  }
  return parsed.origin;
};

const platformUrl = (usgBaseUrl: string, path: string) =>
  `${resolveUsgBaseUrl(usgBaseUrl)}${COMMERCE_PLATFORM_PATH}${path}`;

/** A 200 only satisfies the type at compile time; a missing field would reach a cookie as "undefined". */
const assertTokenResponse = (
  data: BridgeTokenResponse,
  expectRefreshToken = false
): BridgeTokenResponse => {
  if (
    !isValidToken(data?.access_token) ||
    !Number.isFinite(data?.expires_in) ||
    data.expires_in <= 0
  ) {
    throw new Error('Bridge token: response has no usable access token.');
  }
  if (expectRefreshToken && !isValidToken(data.refresh_token)) {
    throw new Error('Bridge token: response has no refresh token.');
  }
  return data;
};

const postToken = async (
  url: string,
  body: URLSearchParams | undefined,
  headers: Record<string, string>
): Promise<BridgeTokenResponse> => {
  try {
    const { data } = await axios.post<BridgeTokenResponse>(url, body, {
      headers,
      timeout: TOKEN_REQUEST_TIMEOUT_MS,
    });
    return data;
  } catch (error) {
    throw new BridgeTokenRequestError(
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

/**
 * Issues a bridge token: a guest token via `client_credentials`, or a user token
 * when a Unity ID token is supplied. Both tokens land in cookies.
 */
export const getBridgeToken = async ({
  usgBaseUrl,
  unityToken,
}: BridgeTokenRequest): Promise<BridgeTokenResponse> => {
  // Omitting the token means guest by design; passing a blank one is a caller bug.
  if (unityToken !== undefined && !isValidToken(unityToken)) {
    throw new Error('Bridge token: unityToken was passed but is blank.');
  }

  const kind: BridgeTokenKind = isValidToken(unityToken) ? 'user' : 'anonymous';

  const data = assertTokenResponse(
    await postToken(
      platformUrl(usgBaseUrl, kind === 'user' ? '/v2/user/token' : '/v2/token'),
      new URLSearchParams({ grant_type: 'client_credentials' }),
      {
        'Content-Type': FORM_URLENCODED,
        ...(kind === 'user' ? { Authorization: `Bearer ${unityToken}` } : {}),
      }
    ),
    true
  );

  storeBridgeAccessToken(kind, data.access_token, data.expires_in);
  storeBridgeRefreshToken(kind, data.refresh_token);

  return data;
};

const refreshBridgeToken = async (
  kind: BridgeTokenKind,
  usgBaseUrl: string
): Promise<BridgeTokenResponse> => {
  const refreshToken = readBridgeRefreshToken(kind);
  if (!isValidToken(refreshToken)) {
    throw new Error(`Bridge token: no ${kind} refresh token stored.`);
  }

  const data = assertTokenResponse(
    await postToken(
      platformUrl(usgBaseUrl, '/v2/token'),
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
      { 'Content-Type': FORM_URLENCODED }
    )
  );

  storeBridgeAccessToken(kind, data.access_token, data.expires_in);

  return data;
};

export const refreshAnonymousBridgeToken = ({
  usgBaseUrl,
}: Pick<BridgeTokenRequest, 'usgBaseUrl'>) =>
  refreshBridgeToken('anonymous', usgBaseUrl);

export const refreshUserBridgeToken = ({
  usgBaseUrl,
}: Pick<BridgeTokenRequest, 'usgBaseUrl'>) =>
  refreshBridgeToken('user', usgBaseUrl);

/**
 * Trades the guest token for a user token on login, carrying the guest cart
 * over. The anonymous cookies are dropped only once the exchange succeeds.
 */
export const exchangeBridgeToken = async ({
  usgBaseUrl,
  unityToken,
  cartId,
}: ExchangeBridgeTokenRequest): Promise<BridgeTokenResponse> => {
  if (!isValidToken(unityToken)) {
    throw new Error('Bridge token: exchange requires a Unity ID token.');
  }

  if (!isValidToken(cartId)) {
    throw new Error('Bridge token: exchange requires a cart id.');
  }

  const anonymousToken = readBridgeAccessToken('anonymous');
  if (!isValidToken(anonymousToken)) {
    throw new Error('Bridge token: exchange requires an anonymous token.');
  }

  const data = assertTokenResponse(
    await postToken(
      platformUrl(
        usgBaseUrl,
        `/v2/token-exchange?cartId=${encodeURIComponent(cartId)}`
      ),
      undefined,
      {
        Authorization: `Bearer ${unityToken}`,
        'X-Authorization': `Bearer ${anonymousToken}`,
      }
    ),
    true
  );

  storeBridgeAccessToken('user', data.access_token, data.expires_in);
  storeBridgeRefreshToken('user', data.refresh_token);
  deleteAnonymousBridgeTokens();

  return data;
};
